import { resultInfo } from "@/constants/report-result";
import { versionDetailPath } from "@/lib/paths";
import { cn } from "@/lib/utils";
import { versionLabel } from "@/lib/version";
import { useNavigate } from "react-router-dom";
import { useApps } from "@/hooks/apps";
import { useUsersByIds } from "@/hooks/users";
import { RocketIcon } from "lucide-react";
import { UserAvatar } from "@/components/user-chip";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { stateInfo } from "@/constants/version-state";
import { VersionProgress } from "./columns";
import { StateBadge } from "@/components/badge/state-badge";
import type { User, Version, VersionReportDoc } from "@/types/domain";

// Everyone working in this version: members of all linked engagements, deduped
// (engagements carry memberIds; people resolve through the /users directory).
const versionMemberIds = (version: Version) => [
  ...new Set(
    (version.engagements ?? []).flatMap(
      (engagement) => engagement.memberIds ?? [],
    ),
  ),
];

// Stacked member avatars (cap 5, +N overflow); full name list in the tooltip.
const MemberStack = ({ members }: { members: User[] }) => {
  const shown = members.slice(0, 5);
  return (
    <span
      className="flex -space-x-1.5"
      title={members.map((m) => m.name).join("、")}
    >
      {shown.map((m) => (
        <UserAvatar
          key={m.id}
          user={m}
          className="h-5 w-5 ring-1 ring-background"
        />
      ))}
      {members.length > shown.length && (
        <span className="z-10 flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[9px] tabular-nums text-muted-foreground ring-1 ring-background">
          +{members.length - shown.length}
        </span>
      )}
    </span>
  );
};

// Result of the latest report (determines the background color of the report count).
const latestResult = (docs: VersionReportDoc[]) => {
  const sorted = [...docs].sort((a, b) => {
    const ta = a.createTime ? new Date(a.createTime).getTime() : 0;
    const tb = b.createTime ? new Date(b.createTime).getTime() : 0;
    return tb - ta;
  });
  return sorted[0]?.result as string | undefined;
};

// Card for a single version (replaces the table row in the list).
// Column layout: [index][app - name][prd][release][report] [timeline][state] — fixed column widths, aligned across rows.
// Click the whole row to open details; the list is read-only: state/rollout/Report edits all happen on the detail page, here we only show counts.
export const VersionCard = ({
  version,
  index,
}: {
  version: Version;
  index: number;
}) => {
  const navigate = useNavigate();
  const apps = useApps();

  const prdCount = version.engagements?.length ?? 0;
  const members = useUsersByIds(versionMemberIds(version));
  // Release records (channel + version-level), counted server-side
  const pubCount = version.releaseCount ?? 0;
  const reportDocs = version.reportDocs || [];
  const reportCount = reportDocs.length;
  const reportResult = resultInfo(latestResult(reportDocs));

  return (
    <RowCard onClick={() => navigate(versionDetailPath(version))}>
      {/* Index */}
      <RowIndex n={typeof version.idx === "number" ? version.idx : index} />

      {/* app - name: flex-1 fills the row; other columns are fixed width, flex space stays constant per row, alignment unchanged */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span
          title={versionLabel(version, apps)}
          className="min-w-0 truncate font-medium"
        >
          {versionLabel(version, apps)}
        </span>
      </div>

      {/* State: read-only badge, fixed-width container keeps following columns aligned */}
      <div className="flex w-24 shrink-0 items-center">
        {version.state !== undefined && <StateBadge state={version.state} />}
      </div>

      {/* prd count: fixed slot (occupies space even when empty); hidden on
          phones so the title keeps its room */}
      <span className="hidden w-14 shrink-0 text-xs tabular-nums text-muted-foreground md:block">
        {prdCount > 0 ? `${prdCount} prd` : ""}
      </span>

      {/* Members across linked engagements — who's working in this version, for
          scanning workload distribution at a glance. */}
      <span className="hidden w-24 shrink-0 lg:flex">
        {members.length > 0 && <MemberStack members={members} />}
      </span>

      {/* Number of release (rollout) records */}
      <span
        title="Release log"
        className="hidden w-10 shrink-0 items-center gap-1 text-xs tabular-nums text-muted-foreground md:flex"
      >
        {pubCount > 0 && (
          <>
            <RocketIcon className="h-3 w-3 text-primary" />
            {pubCount}
          </>
        )}
      </span>

      {/* report: latest result + count */}
      <span
        title="Report"
        className="hidden w-20 shrink-0 items-center gap-1 text-xs tabular-nums text-muted-foreground md:flex"
      >
        {reportCount > 0 && (
          <>
            {reportResult && (
              <span
                className={cn(
                  "whitespace-nowrap rounded px-1.5 py-0.5",
                  reportResult.color,
                )}
              >
                {reportResult.label}
              </span>
            )}
            {reportCount}
          </>
        )}
      </span>

      {/* Timeline: read-only, fixed column width to align with the header; hidden on narrow screens to avoid wrapping */}
      <div className="hidden w-[340px] shrink-0 xl:block">
        <VersionProgress version={version} readOnly />
      </div>
    </RowCard>
  );
};

// Header row: column widths strictly match VersionCard, adding a label to each column.
export const VersionListHeader = () => (
  <div className="flex items-center gap-3 px-3 pb-1 text-xs font-medium text-muted-foreground">
    <span className="w-7 shrink-0">#</span>
    <span className="min-w-0 flex-1">App - Version</span>
    <span className="w-24 shrink-0">State</span>
    <span className="hidden w-14 shrink-0 md:block">PRD</span>
    <span className="hidden w-24 shrink-0 lg:block">Members</span>
    <span className="hidden w-10 shrink-0 md:block">Release</span>
    <span className="hidden w-20 shrink-0 md:block">Report</span>
    <span className="hidden w-[340px] shrink-0 xl:block">Progress</span>
  </div>
);

// List: header + grouping by state (ascending state is the process order, each group has a state-colored separator title) + empty state.
// When groupByState=false, no grouping; render flat in the given order (e.g. the app version page sorted by the online_at timeline).
export const VersionList = ({
  versions,
  isLoading,
  groupByState = true,
}: {
  versions: Version[];
  isLoading?: boolean;
  groupByState?: boolean;
}) => {
  if (versions.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        {isLoading ? <ListLoading /> : <ListEmpty>No versions.</ListEmpty>}
      </div>
    );
  }

  if (!groupByState) {
    return (
      <div className="flex flex-col gap-1">
        <VersionListHeader />
        {versions.map((version, i) => (
          <VersionCard key={version.id} version={version} index={i + 1} />
        ))}
      </div>
    );
  }

  const groups = new Map<number, Version[]>();
  versions.forEach((v) => {
    const s = typeof v.state === "number" ? v.state : -1;
    if (!groups.has(s)) groups.set(s, []);
    groups.get(s)?.push(v);
  });
  const orderedStates = [...groups.keys()].sort((a, b) => a - b);

  let idx = 0;
  return (
    <div className="flex flex-col gap-1">
      <VersionListHeader />
      {orderedStates.map((s) => {
        const st = stateInfo(s);
        const group = groups.get(s) ?? [];
        return (
          <div key={s} className="flex flex-col gap-1">
            <div className="flex items-center gap-2 px-3 pb-0.5 pt-3">
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 text-xs font-medium",
                  st?.color,
                )}
              >
                {st?.label ?? `State ${s}`}
              </span>
              <span className="text-xs text-muted-foreground">
                {group.length}
              </span>
            </div>
            {group.map((version) => {
              idx += 1;
              return (
                <VersionCard key={version.id} version={version} index={idx} />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default VersionCard;
